/**
 * GMU CookieBanner — Shopify ecommerce-event tracker.
 *
 * Schiet GA4-spec dataLayer-events op user-acties die niet via page-load
 * detecteerbaar zijn:
 *   - select_item        (klik op product-tile in collection / featured)
 *   - view_item (re-fire) (variant-keuze op PDP)
 *   - add_to_cart        (POST naar /cart/add.js)
 *   - remove_from_cart   (POST naar /cart/change.js met quantity:0)
 *   - update_cart        (POST naar /cart/update.js voor qty-wijzigingen)
 *
 * Werkt op alle Shopify Online Store 2.0 themes (Dawn, Sense, Refresh,
 * custom Liquid). Theme-agnostisch: hookt via fetch-wrap op de Cart-AJAX-
 * API, niet op DOM-selectors.
 *
 * Voor Shopify Checkout (Plus/2024+ Checkout Extensibility) zijn
 * add_shipping_info/add_payment_info/purchase via Web Pixels te tracken.
 * Standaard storefront-pages dekken we hier.
 */
(function () {
    'use strict';

    const ctx = window.SHOP_CONTEXT || {};
    const dl  = (window.dataLayer = window.dataLayer || []);
    const currency = ctx.shop?.currency || 'EUR';
    const tracking = ctx.tracking || {};

    function push(event, payload) {
        dl.push({ event, ecommerce: payload });
    }

    /**
     * Bouwt een item_id uit het per-shop geconfigureerde template
     * (ctx.tracking.id_format), identiek aan de server-side helper
     * snippets/gmu-item-id.liquid. Default '{sku}'. Placeholders:
     * {country} {product} {variant} {sku} {gtin}. Valt terug op product
     * als de ingevulde template leeg zou zijn.
     */
    function buildItemId(parts) {
        const fmt = tracking.id_format || '{sku}';
        const sub = (k) => (parts[k] != null && parts[k] !== '' ? String(parts[k]) : '');
        const id = fmt
            .replace('{country}', tracking.feed_country || '')
            .replace('{product}', sub('product'))
            .replace('{variant}', sub('variant'))
            .replace('{sku}',     sub('sku'))
            .replace('{gtin}',    sub('gtin'));
        return id || sub('product');
    }

    /**
     * Map een Shopify cart-line of cart-item naar een GA4-item-shape.
     * Cart-API returnt prijzen in centen — delen door 100.
     */
    function mapShopifyItem(item) {
        return {
            item_id:       buildItemId({ product: item.product_id, variant: item.variant_id || item.id, sku: item.sku, gtin: item.barcode }),
            item_name:     String(item.product_title || item.title || ''),
            item_category: String(item.product_type || ''),
            price:         (Number(item.price) || 0) / 100,
            quantity:      Number(item.quantity) || 1,
            currency,
            product_id:    String(item.product_id || item.id),
            quote_item_id: String(item.key || item.id),
        };
    }

    // ---------- select_item: klik op product-tile in lijst ----------------
    // Shopify product-tiles variëren per theme. We luisteren breed op
    // <a href> binnen elementen met `.product-card` / `.card-product` /
    // `[data-product-id]` markup. Dawn-thema gebruikt <a class="card-link">
    // in card-component.
    document.addEventListener('click', (e) => {
        const link = e.target.closest(
            'a.product-card-link, a.card-link, a.product-link, ' +
            '.product-card a[href*="/products/"], .card-product a[href*="/products/"], ' +
            '[data-product-id] a[href*="/products/"]'
        );
        if (!link) return;

        const tile = link.closest('.product-card, .card-product, [data-product-id], [data-product-handle]') || link;
        const items = ctx.items || [];

        // Match-strategieën in volgorde:
        //  1. data-product-id attribute matches ctx.items[].product_id
        //  2. handle uit href matches niets... fallback op 1e item
        const productId = tile.dataset?.productId
                       || tile.dataset?.productHandle
                       || extractProductIdFromHref(link.getAttribute('href'));
        if (productId) {
            const m = items.find(i => String(i.product_id) === String(productId)
                                   || i.item_id === String(productId));
            if (m) {
                push('select_item', {
                    item_list_name: m.item_list_name || '',
                    items: [m],
                });
                return;
            }
        }

        // Fallback: 1e tile-occurrence in lijst pakken (index-positie)
        const allTiles = Array.from(document.querySelectorAll(
            '.product-card, .card-product, [data-product-id]'
        ));
        const idx = allTiles.indexOf(tile);
        if (idx >= 0 && items[idx]) {
            push('select_item', {
                item_list_name: items[idx].item_list_name || '',
                items: [items[idx]],
            });
        }
    }, true);

    function extractProductIdFromHref(href) {
        if (!href) return null;
        // /products/handle?variant=12345 of /products/handle
        const m = href.match(/\/products\/([^/?#]+)/);
        return m ? m[1] : null;
    }

    // ---------- view_item RE-FIRE bij variant-keuze (configurable) -------
    // Shopify themes updaten doorgaans `?variant=ID` in de URL bij swatch-
    // klik (window.history.replaceState) of dispatchen een custom event.
    // We luisteren op popstate + custom 'variant:change'-events + URL-
    // mutatie via MutationObserver op de form's hidden input.
    function fireViewItemForVariantId(variantId) {
        if (!variantId || ctx.page?.type !== 'product' || ctx.product?.type !== 'configurable') return;
        const variant = ctx.product.variants?.[variantId];
        if (!variant) return;
        push('view_item', {
            currency,
            value: variant.price ?? ctx.product.price,
            items: [{
                ...ctx.product,
                item_id:    variant.item_id,
                price:      variant.price,
                product_id: String(variantId),
                variant_of: ctx.product.item_id,
            }],
        });
    }

    // Patroon 1: hidden input mutatie via change-event
    document.addEventListener('change', (e) => {
        const input = e.target;
        if (input?.tagName === 'INPUT' && (input.name === 'id' || input.name === 'variant_id')) {
            fireViewItemForVariantId(input.value);
        }
        // Dawn: variant-radios met data-option-id (swatches)
        if (input?.type === 'radio' && input.closest('variant-radios, variant-selects')) {
            // De form's hidden #ProductSelect-* input wordt geüpdated; wacht
            // 1 tick zodat de Dawn-variant-script eerst draait.
            setTimeout(() => {
                const formInput = document.querySelector('form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] input[name="variant_id"]');
                if (formInput) fireViewItemForVariantId(formInput.value);
            }, 30);
        }
    }, true);

    // Patroon 2: theme-developer hook — analoog aan Magento's gmu:variant-changed
    document.addEventListener('gmu:variant-changed', (e) => {
        fireViewItemForVariantId(e.detail?.variant_id || e.detail?.sku);
    });

    // ---------- AJAX cart-events via fetch-wrap ---------------------------
    // Shopify gebruikt /cart/add.js, /cart/change.js, /cart/update.js,
    // /cart/clear.js. We wrappen window.fetch en parsen body + response.
    const CART_ADD_RE    = /\/cart\/add(\.js|\?|$)/;
    const CART_CHANGE_RE = /\/cart\/change(\.js|\?|$)/;
    const CART_UPDATE_RE = /\/cart\/update(\.js|\?|$)/;

    async function parseBody(body) {
        if (!body) return null;
        try {
            if (body instanceof FormData) {
                const obj = {};
                body.forEach((v, k) => { obj[k] = v; });
                return obj;
            }
            if (body instanceof URLSearchParams) {
                const obj = {};
                body.forEach((v, k) => { obj[k] = v; });
                return obj;
            }
            if (typeof body === 'string') {
                if (body.trim().startsWith('{')) return JSON.parse(body);
                const params = new URLSearchParams(body);
                const obj = {};
                params.forEach((v, k) => { obj[k] = v; });
                return obj;
            }
            if (body && typeof body === 'object') return body;
        } catch { /* ignore */ }
        return null;
    }

    const origFetch = window.fetch?.bind(window);
    if (origFetch) {
        window.fetch = async function (input, opts) {
            const url = typeof input === 'string' ? input : (input?.url || '');
            const reqBody = opts?.body;

            const resp = await origFetch(input, opts);

            // Alleen interpreteren als URL een cart-mutation is
            if (CART_ADD_RE.test(url) || CART_CHANGE_RE.test(url) || CART_UPDATE_RE.test(url)) {
                // Clone response zodat we 'm kunnen lezen zonder consumer te breken
                try {
                    const cloned = resp.clone();
                    const data = await cloned.json();
                    handleCartResponse(url, await parseBody(reqBody), data);
                } catch { /* response was not JSON or already consumed */ }
                // Triggert ook persistTrackingIds() → cart-attributen verversen
                // na elke add/change/update (eigen /cart/update.js valt via de
                // lastSig-guard in een no-op, dus geen lus).
                document.dispatchEvent(new CustomEvent('gmu:cart-changed'));
            }
            return resp;
        };
    }

    function handleCartResponse(url, reqBody, respData) {
        if (CART_ADD_RE.test(url)) {
            // /cart/add.js returnt het toegevoegde line-item-object (of {items:[…]} bij multi-add)
            const added = Array.isArray(respData?.items) ? respData.items : [respData];
            added.forEach(item => {
                if (!item || !item.id) return;
                const ga4 = mapShopifyItem(item);
                push('add_to_cart', {
                    currency,
                    value: ga4.price * ga4.quantity,
                    items: [ga4],
                });
            });
            return;
        }
        if (CART_CHANGE_RE.test(url)) {
            // /cart/change.js: qty kan omhoog OF naar 0 (remove). De response is
            // het volledige cart-object. We parsen de body voor target-line + qty.
            const targetKey = String(reqBody?.id || reqBody?.line || '');
            const targetQty = Number(reqBody?.quantity);
            const items     = respData?.items || [];
            const matched   = items.find(i => String(i.key) === targetKey
                                          || String(i.id)  === targetKey);

            if (targetQty === 0) {
                // Item is verwijderd — niet meer in items[], gebruik ctx.items als source
                const prev = (ctx.items || []).find(i => String(i.quote_item_id) === targetKey
                                                     || String(i.product_id)   === targetKey);
                if (prev) push('remove_from_cart', { currency, items: [prev] });
                return;
            }
            if (matched) {
                // Qty-update → fire add_to_cart of remove_from_cart op verschil
                // We weten niet de oude qty zonder state; pragmatisch: add_to_cart
                // met de huidige qty (downstream tools maken zelf 't verschil).
                const ga4 = mapShopifyItem(matched);
                push('add_to_cart', {
                    currency,
                    value: ga4.price * ga4.quantity,
                    items: [ga4],
                });
            }
            return;
        }
        if (CART_UPDATE_RE.test(url)) {
            // /cart/update.js — bulk update. Te complex om individuele add/remove
            // events af te leiden zonder pre/post-diff. Skippen.
        }
    }

    // ---------- Generic theme hook ---------------------------------------
    // Shopify themes verschillen flink in hoe ze cart-events naar de
    // frontend communiceren. Voor downstream tools dispatchen we een
    // stabiele `gmu:cart-changed` event na elke cart-mutation.
    document.addEventListener('cart:update', () => {
        document.dispatchEvent(new CustomEvent('gmu:cart-changed'));
    });
    // Dawn-thema pubsub equivalent
    document.addEventListener('cart-update', () => {
        document.dispatchEvent(new CustomEvent('gmu:cart-changed'));
    });

    // ---------- GA4/Ads-identifiers → cart-attributen --------------------
    // De Shopify Checkout-pixel (Customer Events) draait in een sandbox en
    // kan de storefront-cookies (_ga / _ga_<stream> / _gcl_*) niet
    // betrouwbaar lezen. Daarom leggen we client_id, session_id en de
    // Google-klik-IDs hier vast — waar de cookies wél leesbaar zijn — als
    // cart-attributen. Die reizen mee naar de checkout en komen in de pixel
    // binnen via `checkout.attributes`, zodat de purchase aan dezelfde
    // GA4-sessie/bron blijft hangen (geen Session source/medium = "(not set)"
    // meer, en geen dubbele random client_ids bij re-fire).
    (function persistTrackingIds() {
        function readCookie(name) {
            const safe = name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1');
            const m = document.cookie.match('(?:^|; )' + safe + '=([^;]*)');
            return m ? decodeURIComponent(m[1]) : '';
        }
        // client_id uit _ga  (GA1.1.<cid1>.<cid2>  ->  "<cid1>.<cid2>")
        function gaClientId() {
            const m = readCookie('_ga').match(/GA\d\.\d\.(\d+\.\d+)/);
            return m ? m[1] : '';
        }
        // session_id uit _ga_<stream>. Stream optioneel via
        // SHOP_CONTEXT.tracking.ga4_stream, anders auto-detect 1e _ga_*-cookie.
        // Ondersteunt beide GA4-cookieformaten:
        //   GS1.1.<sid>.<n>...       (ouder)
        //   GS2.1.s<sid>$o1$g1$...   (nieuwer, $-gescheiden, 's'-prefix)
        function gaSessionId() {
            const stream = tracking.ga4_stream;
            let val = stream ? readCookie('_ga_' + stream) : '';
            if (!val) {
                const m = document.cookie.match(/_ga_([A-Z0-9]+)=GS\d\.\d\.s?\d+/);
                if (m) val = readCookie('_ga_' + m[1]);
            }
            const sm = val.match(/GS\d\.\d\.s?(\d+)/);
            return sm ? sm[1] : '';
        }

        function collect() {
            const a = {};
            const cid = gaClientId();   if (cid) a['_ga_cid'] = cid;
            const sid = gaSessionId();  if (sid) a['_ga_sid'] = sid;
            const aw = readCookie('_gcl_aw'); if (aw) a['_gcl_aw'] = aw;
            const dc = readCookie('_gcl_dc'); if (dc) a['_gcl_dc'] = dc;
            const au = readCookie('_gcl_au'); if (au) a['_gcl_au'] = au;
            try {
                const q = new URLSearchParams(location.search);
                const gclid = q.get('gclid') || q.get('wbraid') || q.get('gbraid') || '';
                if (gclid) a['_ga_gclid'] = gclid;
            } catch (e) { /* ignore */ }
            return a;
        }

        let lastSig = '';
        function persist() {
            const attrs = collect();
            if (!attrs['_ga_cid']) return;        // gtag nog niet geladen → later opnieuw
            const sig = JSON.stringify(attrs);
            if (sig === lastSig) return;          // niets veranderd → geen extra request
            lastSig = sig;
            try {
                // De fetch-wrap hierboven onderschept /cart/update.js maar
                // routeert het naar de CART_UPDATE skip-branch (geen
                // ecommerce-event, geen lus) — dus veilig om te gebruiken.
                fetch('/cart/update.js', {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body:    JSON.stringify({ attributes: attrs }),
                    keepalive: true,
                }).catch(() => {});
            } catch (e) { /* ignore */ }
        }

        // 1) bij page-load: blijf proberen tot gtag _ga heeft gezet (na consent)
        let tries = 0;
        (function tryPersist() {
            if (gaClientId()) { persist(); return; }
            if (tries++ < 8) setTimeout(tryPersist, 1500);   // ~12s venster voor consent
        })();
        // 2) vlak vóór de checkout (klik op checkout-knop / form-submit)
        document.addEventListener('click', (e) => {
            if (e.target.closest('[name="checkout"], a[href*="/checkout"], button[href*="/checkout"]')) persist();
        }, true);
        document.addEventListener('submit', (e) => {
            const action = e.target?.getAttribute?.('action') || '';
            if (/\/cart|\/checkout/.test(action)) persist();
        }, true);
        // 3) na elke cart-mutatie (nieuw cart-token kan attributen resetten)
        document.addEventListener('gmu:cart-changed', persist);
    })();
})();
